// Parse a charging-curver `*_data.csv` (produced by the charging-curver project)
// into the pieces the Ladekurver chart needs: the SoC->kW/A curve and the summary
// metrics. The file has three sections separated by blank lines:
//
//   elapsed_min,soc_pct,kw,volt,amp      <- per-sample time series (ignored here)
//   ...
//   soc_pct,kw[,amp]                     <- the curve (what we plot)
//   ...
//   metric,value                         <- summary metrics
//   ...
//
// kW-only sources (e.g. the cluster-screen clip) omit the amp column.

export function parseCurveCsv(text) {
  const rows = text.split(/\r?\n/).map((l) => l.split(',').map((s) => s.trim()));

  const hi = rows.findIndex((r) => r[0] === 'soc_pct' && r[1] === 'kw');
  if (hi === -1) {
    throw new Error('Fant ikke kurve-seksjonen («soc_pct,kw») i CSV-filen.');
  }
  const hasAmpCol = rows[hi][2] === 'amp';

  const curve = [];
  for (let i = hi + 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r[0]) break; // blank line ends the section
    const soc = parseFloat(r[0]);
    const kw = parseFloat(r[1]);
    if (!Number.isFinite(soc) || !Number.isFinite(kw)) break;
    const ampRaw = hasAmpCol ? parseFloat(r[2]) : NaN;
    curve.push({ soc, kw, amp: Number.isFinite(ampRaw) ? ampRaw : null });
  }
  if (curve.length < 2) {
    throw new Error('Kurven må ha minst to punkter.');
  }
  curve.sort((a, b) => a.soc - b.soc);

  const metrics = {};
  const mi = rows.findIndex((r) => r[0] === 'metric' && r[1] === 'value');
  if (mi !== -1) {
    for (let i = mi + 1; i < rows.length; i++) {
      const r = rows[i];
      if (r[0]) metrics[r[0]] = r[1];
    }
  }

  const hasAmp = curve.some((p) => p.amp !== null);
  return { curve, metrics, hasAmp };
}

// Pretty Norwegian labels + formatting for the known charging-curver metrics.
export function summarizeMetrics(metrics = {}) {
  const out = [];
  const num = (v) => (v === undefined ? null : Number(v));
  if (metrics.soc_start !== undefined && metrics.soc_end !== undefined) {
    out.push({ label: 'Ladenivå', value: `${metrics.soc_start}–${metrics.soc_end} %` });
  }
  if (metrics.peak_kw !== undefined) {
    out.push({
      label: 'Maks effekt',
      value: `${Math.round(num(metrics.peak_kw))} kW` +
        (metrics.peak_kw_at_soc !== undefined ? ` @ ${metrics.peak_kw_at_soc} %` : '')
    });
  }
  if (metrics.peak_amp !== undefined) {
    out.push({
      label: 'Maks strøm',
      value: `${Math.round(num(metrics.peak_amp))} A` +
        (metrics.peak_amp_at_soc !== undefined ? ` @ ${metrics.peak_amp_at_soc} %` : '')
    });
  }
  if (metrics.avg_charging_speed_kw !== undefined) {
    out.push({ label: 'Snitteffekt', value: `${Math.round(num(metrics.avg_charging_speed_kw))} kW` });
  }
  if (metrics.time_to_80pct_mmss !== undefined) {
    out.push({ label: 'Til 80 %', value: `${metrics.time_to_80pct_mmss} min` });
  }
  if (metrics.time_to_100pct_mmss !== undefined) {
    out.push({ label: 'Til 100 %', value: `${metrics.time_to_100pct_mmss} min` });
  }
  if (metrics.charge_duration_mmss !== undefined) {
    out.push({ label: 'Varighet', value: `${metrics.charge_duration_mmss} min` });
  }
  return out;
}
